import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Snackbar,
  Typography,
} from '@mui/material';
import { MultilineTooltip } from 'components/common/simpleInputFields/multilineTooltip';
import { formatTranslation } from 'intl';
import BackgroundLetterAvatar from 'components/core/Avatar/Avatar';
import { ReactElement, useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { isAdmin } from 'shared/domain/role/isAdmin';
import { isVisibleNoAccess } from 'shared/domain/role/isVisibleNoAccess';
import { AccessLevelModel } from 'shared/domain/issue/mapping/accessLevel/model/types';
import { translateRoleTooltipMessage } from 'shared/domain/user/mapping/toView';
import {
  UserOnViewWithIssueAccessLevel,
  isUserOnViewWithIssueAccessLevel,
} from 'shared/domain/user/types/view';
import { inclusiveGray } from 'shared/utils/inclusiveColors';
import { anyToSupportedISO6391LanguageCode } from 'shared/utils/locale/anyToSupportedISO6391LanguageCode';
import { getHelpPageAboutUserRolesUrl } from 'shared/utils/locale/getHelpPageUrl';
import EventDialog, {
  EventDialogActionNames,
} from 'views/issue/events/EventDialog/EventDialog';
import { AccessLevelSelect } from './accessLevelSelect';
import { AccessErrorMessage } from './cardActions/accessErrorMessage';
import { CallByPhoneIcon } from './cardActions/callByPhone';
import { CreateEventWithMentionedUserChip } from './cardActions/createEventWithMentionedUser';
import { EditUserIcon } from './cardActions/editUser';
import { GoToUserIcon } from './cardActions/openDetailedView';
import { PhoneNumber } from './phoneNumber';
import { AccessErrorType, UserCardProps, UserCardVariant } from './types';
import { UserEmail } from './userEmail';
import { IssueTranslationKeys, NotificationTranslationKeys } from 'intl/keys';

export function UserCard({
  user,
  userAccesses,
  issueId,
  siteId,
  processId,
  sx,
  variant = UserCardVariant.standard,
  accessError,
  currentSiteName,
  currentProcessName,
}: UserCardProps): ReactElement {
  const intl = useIntl();
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [eventDialogOpen, setEventDialogOpen] = useState<boolean>(false);
  const handleEventDialogClose = useCallback(() => {
    setEventDialogOpen(false);
  }, []);
  const hasAccessToCurrentSite =
    isAdmin(user.role._id) ||
    user.sites.some((site) => site._id === siteId);
  const hasAccessToCurrentProcess =
    isAdmin(user.role._id) ||
    user.processes.some((process) => process._id === processId);
  const userWithAccessLevel = isUserOnViewWithIssueAccessLevel(user) ? user : null;
  const hasNoIssueAccess = userWithAccessLevel?.accessLevel.value === AccessLevelModel.NONE;
  const userForAccessSelect: UserOnViewWithIssueAccessLevel | null =
    userAccesses &&
    issueId &&
    siteId &&
    processId &&
    userWithAccessLevel
      ? userWithAccessLevel
      : null;

  const isErrorVariant = variant === UserCardVariant.withAccessError;

  return (
    <Box sx={sx} key={user._id}>
      <Card
        sx={{
          minWidth: 380,
          maxWidth: 520,
          borderRadius: 2,
          backgroundColor: inclusiveGray[50],
        }}
        raised={true}
      >
        {/* Header: Avatar + Name + Email + Phone + Actions */}
        <CardContent sx={{ pb: 1 }}>
          <Box display='flex' alignItems='flex-start' gap={2}>
            <BackgroundLetterAvatar
              userName={user.label}
              email={user.email}
            />
            <Box flex={1}>
              <Typography
                fontWeight={500}
                fontSize={18}
                sx={{ wordBreak: 'break-word' }}
              >
                {user.label}
              </Typography>
              <UserEmail
                email={user.email}
                setOpenSnackbar={setOpenSnackbar}
              />
              <PhoneNumber
                phone={user.phone}
                setOpenSnackbar={setOpenSnackbar}
              />
              {/* Actions row: GoTo + Call + Edit + Write message */}
              <Box display='flex' alignItems='center' gap={1} mt={1}>
                <GoToUserIcon userId={user._id} />
                <CallByPhoneIcon phone={user.phone} />
                <EditUserIcon
                  userId={user._id}
                  userRole={user.role._id}
                />
                <CreateEventWithMentionedUserChip
                  setEventDialogOpen={setEventDialogOpen}
                />
              </Box>
            </Box>
          </Box>
        </CardContent>

        <Divider sx={{ my: 1 }} />

        {/* Role section: Role + AccessLevelSelect */}
        <CardContent sx={{ pt: 0.5, pb: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Box
            display='flex'
            alignItems='center'
            justifyContent='space-between'
          >
            <MultilineTooltip
              message={translateRoleTooltipMessage(
                user.role._id,
                intl
              )}
              intl={intl}
              footerLink={getHelpPageAboutUserRolesUrl(
                anyToSupportedISO6391LanguageCode(intl.locale)
              )}
            >
              <Chip
                label={user.role.label}
                variant='outlined'
                size='small'
                sx={{
                  border: 'none',
                  color: 'black',
                  ml: -1,
                  '& .MuiChip-label': {
                    wordSpacing: '4px',
                  },
                }}
              />
            </MultilineTooltip>

            {userForAccessSelect && (
              <AccessLevelSelect
                userAccesses={userAccesses!}
                user={userForAccessSelect}
                issueId={issueId!}
                siteId={siteId!}
                processId={processId!}
              />
            )}
          </Box>

          {/* Access error messages */}
          {isVisibleNoAccess(user.role._id) && (
            <AccessErrorMessage
              accessError={{
                type: AccessErrorType.noPermissionsRole,
              }}
            />
          )}
          {!isVisibleNoAccess(user.role._id) && !hasAccessToCurrentProcess && (
            <AccessErrorMessage
              accessError={{
                type: AccessErrorType.noProcessAccess,
                entityName: currentProcessName,
              }}
            />
          )}
          {!isVisibleNoAccess(user.role._id) && !hasAccessToCurrentSite && (
            <AccessErrorMessage
              accessError={{
                type: AccessErrorType.noSiteAccess,
                entityName: currentSiteName,
              }}
            />
          )}
          {hasNoIssueAccess && (
            <AccessErrorMessage
              accessError={{
                type: AccessErrorType.noIssueAccess,
              }}
            />
          )}

          {/* Custom error message (e.g. account deleted) */}
          {isErrorVariant && accessError && (
            <AccessErrorMessage accessError={accessError} />
          )}
        </CardContent>
      </Card>
      <Box
        onMouseLeave={(e) => {
          // prevents the event from bubbling up to the Popper which results in closing the Dialog
          e.stopPropagation();
          e.preventDefault();
        }}
      >
        {eventDialogOpen && (
          <EventDialog
            onClose={handleEventDialogClose}
            isOpen={eventDialogOpen}
            action={{
              actionName: EventDialogActionNames.create,
              params: {
                title: formatTranslation(intl, IssueTranslationKeys.eventsDialogTitleEdit,),
                initialMentions: [user],
              },
            }}
          />
        )}
        <Snackbar
          open={openSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          autoHideDuration={2000}
          onClose={() => setOpenSnackbar(false)}
          message={formatTranslation(intl, NotificationTranslationKeys.contentCopiedToClipboard,)}
        />
      </Box>
    </Box>
  );
}
